import '../styles/buttons.css';
import React, { useCallback } from 'react';
import { FaTimes } from 'react-icons/fa';
import { ReceivableItem } from '../types/operations';

interface ReceivableModalViewProps {
    isOpen: boolean;
    onClose: () => void;
    receivable: ReceivableItem | null;
}

const ReceivableModalView: React.FC<ReceivableModalViewProps> = React.memo(({ isOpen, onClose, receivable }) => {
    const formatCurrency = useCallback((value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    }, []);

    const formatDate = useCallback((dateString: string) => {
        if (!dateString) return '-';
        // Extrair YYYY-MM-DD e converter para DD/MM/YYYY sem timezone
        const dateOnly = dateString.split('T')[0];
        const [year, month, day] = dateOnly.split('-');
        return `${day}/${month}/${year}`;
    }, []);

    // Early return para performance
    if (!isOpen || !receivable) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Detalhes do Recebível
                    </h2>
                    <button type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                        <FaTimes size={20} />
                    </button>
                </div>

                <div className="p-6 space-y-6 overflow-y-auto flex-1">
                    {/* Informações do Documento */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Documento</h3>
                            <div className="space-y-2">
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">ID do Documento</span>
                                    <p className="text-sm text-gray-900 dark:text-white">{receivable.document_id || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Título</span>
                                    <p className="text-sm text-gray-900 dark:text-white">{receivable.document_title || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">CFOP</span>
                                    <p className="text-sm text-gray-900 dark:text-white">{receivable.cfop || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Data de Emissão</span>
                                    <p className="text-sm text-gray-900 dark:text-white">{formatDate(receivable.issue_date)}</p>
                                </div>
                            </div>
                        </div>

                        {/* Informações do Cedente */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Cedente</h3>
                            <div className="space-y-2">
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Nome</span>
                                    <p className="text-sm text-gray-900 dark:text-white">{receivable.assignor_name || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">CNPJ</span>
                                    <p className="text-sm text-gray-900 dark:text-white">{receivable.assignor_cnpj || '-'}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Informações do Devedor */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Devedor</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Nome</span>
                                    <p className="text-sm text-gray-900 dark:text-white">{receivable.debtor_name || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">CPF/CNPJ</span>
                                    <p className="text-sm text-gray-900 dark:text-white">{receivable.debtor_cpf_cnpj || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Email</span>
                                    <p className="text-sm text-gray-900 dark:text-white">{receivable.debtor_email || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Telefone</span>
                                    <p className="text-sm text-gray-900 dark:text-white">{receivable.address_phone || '-'}</p>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Endereço</span>
                                    <p className="text-sm text-gray-900 dark:text-white">
                                        {[
                                            receivable.address_street,
                                            receivable.address_number,
                                            receivable.address_complement
                                        ].filter(Boolean).join(', ') || '-'}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Bairro</span>
                                    <p className="text-sm text-gray-900 dark:text-white">{receivable.address_district || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Cidade/Estado</span>
                                    <p className="text-sm text-gray-900 dark:text-white">
                                        {[receivable.address_city, receivable.address_state].filter(Boolean).join('/') || '-'}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">CEP</span>
                                    <p className="text-sm text-gray-900 dark:text-white">{receivable.address_zip_code || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Código IBGE</span>
                                    <p className="text-sm text-gray-900 dark:text-white">{receivable.address_ibge_code || '-'}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Informações do Recebível */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Recebível</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Número</span>
                                    <p className="text-sm text-gray-900 dark:text-white">{receivable.receivable_number || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Data de Vencimento</span>
                                    <p className="text-sm text-gray-900 dark:text-white">{formatDate(receivable.receivable_due_date)}</p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Valor</span>
                                    <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                                        {formatCurrency(receivable.receivable_amount)}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Valor do Desembolso</span>
                                    <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                                        {formatCurrency(receivable.disbursement_value || 0)}
                                    </p>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">V. Pag</span>
                                    <p className="text-sm text-gray-900 dark:text-white">{receivable.receivable_v_pag || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Natureza da Operação</span>
                                    <p className="text-sm text-gray-900 dark:text-white">{receivable.receivable_nat_op || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Indicador de Pagamento</span>
                                    <p className="text-sm text-gray-900 dark:text-white">{receivable.receivable_ind_pag || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">% Aval da Operação</span>
                                    <p className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                                        {receivable.guarantee_percent ? `${receivable.guarantee_percent.toFixed(2)}%` : '-'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Informações do Indexador */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Indexador</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Nome</span>
                                <p className="text-sm text-gray-900 dark:text-white">{receivable.fee_name || '-'}</p>
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Valor</span>
                                <p className="text-sm font-semibold text-orange-600 dark:text-orange-400">
                                    {receivable.fee_value ? `${receivable.fee_value.toFixed(2)}%` : '-'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Informações Adicionais */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Informações Adicionais</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Risco Principal</span>
                                <p className="text-sm text-gray-900 dark:text-white">{receivable.main_risk || '-'}</p>
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Card do JIRA</span>
                                <p className="text-sm text-gray-900 dark:text-white">{receivable.external_link || '-'}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
                    <button type="button"
                        onClick={onClose}
                        className="text-white bg-gradient-to-r from-gray-500 via-gray-600 to-gray-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium rounded text-sm px-5 py-2.5 text-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    );
});

ReceivableModalView.displayName = 'ReceivableModalView';

export default ReceivableModalView;